import { useEffect, useState } from "react";
import { fetchItemDetail } from "../api";
import { syncedReviewPlatform } from "./ReviewSyncToast";
import { C } from "../tokens";

export interface ReviewCompletionData {
  key: string;
  name: string;
  accepted: Array<{ id: string; startedAt: number }>;
  rejected: number;
  deferred: number;
  hasDeferredItems?: boolean;
}

export interface ReviewSyncCounts { procore: number; autodesk: number; waiting: number }

/** Where this session's accepted records have CONFIRMED syncing to — read
 * from each record's own sync state, with a timestamp newer than its accept,
 * polled while any is still waiting. `result` must keep its identity across
 * renders; a new object re-reads from scratch. */
export function useReviewSyncCounts(result: Pick<ReviewCompletionData, "accepted">, search: string): ReviewSyncCounts {
  const [snapshot, setSnapshot] = useState<{ result: Pick<ReviewCompletionData, "accepted">; search: string; counts: ReviewSyncCounts } | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async (attempt: number) => {
      const counts = { procore: 0, autodesk: 0, waiting: 0 };
      let next = 0;
      await Promise.all(Array.from({ length: Math.min(4, result.accepted.length) }, async () => {
        while (!cancelled && next < result.accepted.length) {
          const item = result.accepted[next++]!;
          try {
            const detail = await fetchItemDetail(item.id, search);
            const provider = syncedReviewPlatform(detail.integrationSync, item.startedAt);
            if (provider === "Procore") counts.procore += 1;
            else if (provider === "Autodesk") counts.autodesk += 1;
            else if (detail.item.assigneeDirectory || detail.integrationSync?.procore?.status || detail.integrationSync?.autodesk?.status) counts.waiting += 1;
          } catch { counts.waiting += 1; }
        }
      }));
      if (cancelled) return;
      setSnapshot({ result, search, counts });
      if (counts.waiting && attempt < 29) timer = setTimeout(() => void poll(attempt + 1), 2000);
    };
    void poll(0);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [result, search]);
  return snapshot?.result === result && snapshot.search === search ? snapshot.counts : { procore: 0, autodesk: 0, waiting: 0 };
}

/** "Synced 2 items to Procore · 1 item accepted" — what the confirmed sync
 *  says about this session's accepts. */
export function syncOutcome(acceptedCount: number, sync: ReviewSyncCounts): string[] {
  const acceptedOnly = acceptedCount - sync.procore - sync.autodesk;
  return [
    sync.procore ? `Synced ${sync.procore} ${sync.procore === 1 ? "item" : "items"} to Procore` : "",
    sync.autodesk ? `Synced ${sync.autodesk} ${sync.autodesk === 1 ? "item" : "items"} to Autodesk` : "",
    acceptedOnly ? `${acceptedOnly} ${acceptedOnly === 1 ? "item" : "items"} accepted` : "",
  ].filter(Boolean);
}

/** Acceptance is durable before this renders. Sync is reported only after
 * reading a confirmed result, including a fresh timestamp for this session. */
export function ReviewCompletion({ result, search, onDismiss }: {
  result: ReviewCompletionData; search: string; onDismiss: () => void;
}) {
  const sync = useReviewSyncCounts(result, search);
  return <div data-testid="review-completion" role="status" style={{ flexShrink: 0, padding: "14px 24px", background: C.greenSoft, borderBottom: `1px solid ${C.line}`, display: "flex", gap: 12 }}>
    <div style={{ flex: 1, minWidth: 0, fontSize: 14.95, lineHeight: 1.5 }}>
      <strong>{result.name} — {result.deferred || result.hasDeferredItems ? "review session finished" : "review complete"}</strong>
      <div data-testid="review-completion-outcome">{[
        ...syncOutcome(result.accepted.length, sync),
        result.rejected ? `${result.rejected} rejected` : "",
        result.deferred ? `${result.deferred} saved for later` : "",
      ].filter(Boolean).join(" · ")}</div>
      {!!sync.waiting && <div>{sync.waiting} {sync.waiting === 1 ? "item has" : "items have"} no confirmed sync yet.</div>}
    </div>
    <button className="kb" type="button" aria-label="Dismiss review summary" onClick={onDismiss} style={{ alignSelf: "start", color: C.sub }}>×</button>
  </div>;
}
